import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import {
  mapReviewError,
  merchantReviewResponseSchema,
  useRemoveReviewResponse,
  useUpsertReviewResponse,
} from '@dukanti/shared';
import type { AdminProductReviewRow, MerchantReviewResponseFormValues } from '@dukanti/shared';
import { useTranslation } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

interface ReviewResponseDialogProps {
  readonly review: AdminProductReviewRow | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function ReviewResponseDialog({
  review,
  open,
  onOpenChange,
}: ReviewResponseDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const upsertMutation = useUpsertReviewResponse(supabase);
  const removeMutation = useRemoveReviewResponse(supabase);
  const form = useForm<MerchantReviewResponseFormValues>({
    resolver: zodResolver(merchantReviewResponseSchema),
    defaultValues: { responseText: '' },
  });

  useEffect(() => {
    if (open) form.reset({ responseText: review?.merchant_response_text ?? '' });
  }, [form, open, review?.merchant_response_text]);

  const submit = form.handleSubmit((values) => {
    if (!review) return;
    upsertMutation.mutate(
      { reviewId: review.id, responseText: values.responseText },
      { onSuccess: () => onOpenChange(false) },
    );
  });
  const error = upsertMutation.error ?? removeMutation.error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} data-testid="review-response-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.reviews.response.title')}</DialogTitle>
          <DialogDescription>{review?.product_name_snapshot ?? ''}</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          {error ? (
            <p className="text-destructive text-start text-sm">{t(mapReviewError(error))}</p>
          ) : null}
          <Textarea
            rows={5}
            placeholder={t('admin.reviews.response.placeholder')}
            aria-invalid={!!form.formState.errors.responseText}
            {...form.register('responseText')}
          />
          {form.formState.errors.responseText?.message ? (
            <p className="text-destructive text-start text-sm">
              {t(form.formState.errors.responseText.message as never)}
            </p>
          ) : null}
          <DialogFooter>
            {review?.merchant_response_text ? (
              <Button
                type="button"
                variant="outline"
                disabled={removeMutation.isPending || upsertMutation.isPending}
                onClick={() =>
                  review &&
                  removeMutation.mutate(
                    { reviewId: review.id },
                    { onSuccess: () => onOpenChange(false) },
                  )
                }
              >
                <Trash2 className="size-4" aria-hidden />
                {t('admin.reviews.actions.removeResponse')}
              </Button>
            ) : null}
            <Button type="submit" disabled={upsertMutation.isPending || removeMutation.isPending}>
              {upsertMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              {t('admin.reviews.response.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
